'use client';

import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { Box, Button, Paper, Tooltip, Typography } from '@mui/material';
import { Check, Copy, Share2 } from 'lucide-react';

import { useT } from '@/hooks';

import { MESSAGES } from '@/constants/messages.constants';

interface ReferralLinkCardProps {
  code: string;
  isAmbassador?: boolean;
}

/**
 * ReferralLinkCard Component
 * Displays the user's unique referral link with a copy-to-clipboard action using MUI.
 */
export const ReferralLinkCard: React.FC<ReferralLinkCardProps> = ({ code, isAmbassador }) => {
  const { t } = useT();
  const [copied, setCopied] = useState(false);
  const referralUrl = `https://porvenix.com/r/${code}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(referralUrl);
      setCopied(true);
      toast.success(MESSAGES.REFERRAL.LINK_COPIED);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error(MESSAGES.REFERRAL.COPY_FAILED);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join Porvenix',
          text: 'Trade on Polymarket with Porvenix using my referral link!',
          url: referralUrl,
        });
      } catch (err) {
        // User cancelled share or other error
      }
    } else {
      handleCopy();
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 4,
        p: 3,
        position: 'relative',
        overflow: 'hidden',
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          right: 0,
          width: '30%',
          height: '100%',
          background: 'linear-gradient(to left, rgba(37, 99, 235, 0.05), transparent)',
          pointerEvents: 'none',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography variant="subtitle1" fontWeight={700} color="text.primary">
              {t('referral.yourReferralLink')}
            </Typography>
            {isAmbassador && (
              <Box
                sx={{
                  px: 1,
                  py: 0.25,
                  borderRadius: 10,
                  bgcolor: 'primary.lighter',
                  color: 'primary.main',
                  fontSize: '0.625rem',
                  fontWeight: 800,
                  textTransform: 'uppercase',
                  border: '1px solid',
                  borderColor: 'primary.light',
                }}
              >
                {t('referral.ambassador')}
              </Box>
            )}
          </Box>
          <Typography variant="body2" color="text.secondary">
            {t('referral.shareLinkDesc')}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            width: { xs: '100%', md: 'auto' },
          }}
        >
          <Box
            sx={{
              bgcolor: 'action.hover',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2.5,
              px: 2,
              py: 1,
              fontFamily: 'monospace',
              fontSize: '0.875rem',
              color: 'text.primary',
              flexGrow: { xs: 1, md: 0 },
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: { xs: 'none', md: 300 },
            }}
          >
            {referralUrl.replace(/^https?:\/\//, '')}
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title={copied ? 'Copied!' : 'Copy Link'}>
              <Button
                variant="contained"
                onClick={handleCopy}
                sx={{
                  minWidth: 44,
                  width: 44,
                  height: 44,
                  borderRadius: 2.5,
                  p: 0,
                  bgcolor: 'primary.main',
                  '&:hover': { bgcolor: 'primary.dark' },
                }}
              >
                {copied ? <Check size={18} /> : <Copy size={18} />}
              </Button>
            </Tooltip>

            <Tooltip title="Share Link">
              <Button
                variant="outlined"
                onClick={handleShare}
                sx={{
                  minWidth: 44,
                  width: 44,
                  height: 44,
                  borderRadius: 2.5,
                  p: 0,
                  borderColor: 'divider',
                  color: 'text.primary',
                  '&:hover': { borderColor: 'text.secondary', bgcolor: 'action.hover' },
                }}
              >
                <Share2 size={18} />
              </Button>
            </Tooltip>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};
